import { Variant } from 'shared/types/product';

/**
 * The variant-defining attributes that lead the PDP, in priority order ("Species", then
 * "Material"). product-information.tsx renders these above the finish sections, and
 * `usePrefetchLeadAxisImages` warms images for the other values of whichever is present —
 * these are the identity choices shoppers flip between. Shared here so the selector order and
 * the prefetch target can never drift.
 */
export const LEAD_ATTRIBUTE_KEYS = ['wood-type', 'material'];

/**
 * The variant's raw species value, or `undefined` when it has none (powder-coated metal,
 * hardware, …). No SKU fallback, so callers that need to distinguish "no species" from
 * "species happens to equal the SKU" — such as the species-scoped finish image tag — can skip
 * the lookup entirely.
 */
export const getVariantSpeciesAttribute = (variant: Variant): string | undefined => {
  for (const key of LEAD_ATTRIBUTE_KEYS) {
    const value = variant.attributes?.[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
};

/**
 * Normalizes a display value into a Cloudinary tag segment: "White Oak" → "white-oak".
 *
 * Cloudinary tag values are restricted to `[a-zA-Z0-9_-]` (see the backend AssetController),
 * but species comes from a commercetools attribute holding human-readable text. Anyone
 * tagging assets in Cloudinary must use this same slug — a mismatch is silent, yielding no
 * species image and falling back to the product's own gallery.
 *
 * Every disallowed run becomes a single hyphen rather than being deleted, so separators are
 * preserved: "Hickory/Pecan" → "hickory-pecan", not "hickorypecan". Deleting them would let
 * two distinct species collapse onto the same slug and silently share one image tier. This
 * deliberately differs from the cart custom-object key slug in CartController, which strips
 * disallowed characters; that key identifies a cart line, so collisions there are harmless.
 */
export const toTagSlug = (value: string): string =>
  value
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
